import { BudgetRuleCampaignAssociations } from '@/components/BudgetRuleCampaignAssociations';
import { BudgetRuleLinkedCampaignsChips } from '@/components/BudgetRuleLinkedCampaignsChips';
import { useBudgetRuleAssociatedCampaigns } from '@/hooks/useBudgetRuleAssociatedCampaigns';
import type { CampaignAdProduct, SpCampaignNormalized } from '@/types/spCampaigns';

/** Amazon list endpoint returns rules under `budgetRulesForAdvertiserResponse` with nested `ruleDetails`. */
export function extractBudgetRulesList(data: unknown): Record<string, unknown>[] {
  if (Array.isArray(data)) {
    return data.filter((x): x is Record<string, unknown> => !!x && typeof x === 'object');
  }
  if (!data || typeof data !== 'object') return [];
  const d = data as Record<string, unknown>;
  const fromAdvertiser = d.budgetRulesForAdvertiserResponse;
  if (Array.isArray(fromAdvertiser)) {
    return fromAdvertiser.filter((x): x is Record<string, unknown> => !!x && typeof x === 'object');
  }
  const associatedRules = d.associatedRules;
  if (Array.isArray(associatedRules)) {
    return associatedRules.filter((x): x is Record<string, unknown> => !!x && typeof x === 'object');
  }
  const list =
    (d.budgetRules as unknown[]) ||
    (d.budgetRulesDetails as unknown[]) ||
    (d.items as unknown[]) ||
    [];
  return Array.isArray(list)
    ? list.filter((x): x is Record<string, unknown> => !!x && typeof x === 'object')
    : [];
}

export function getBudgetRulesNextToken(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null;
  const t = (data as Record<string, unknown>).nextToken;
  return typeof t === 'string' && t ? t : null;
}

function formatAmazonYyyymmdd(s: string | undefined | null): string {
  if (s == null || typeof s !== 'string' || s.length !== 8) return '—';
  const y = s.slice(0, 4);
  const m = s.slice(4, 6);
  const day = s.slice(6, 8);
  const dt = new Date(`${y}-${m}-${day}T12:00:00Z`);
  if (Number.isNaN(dt.getTime())) return s;
  return dt.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function comparisonOperatorLabel(op: string): string {
  const map: Record<string, string> = {
    LESS_THAN_OR_EQUAL_TO: 'is less than or equal to',
    LESS_THAN: 'is less than',
    GREATER_THAN_OR_EQUAL_TO: 'is greater than or equal to',
    GREATER_THAN: 'is greater than',
  };
  return map[op] ?? op.replace(/_/g, ' ').toLowerCase();
}

function ruleTypeLabel(t: string): string {
  const map: Record<string, string> = {
    SCHEDULE: 'Scheduled',
    PERFORMANCE: 'Performance',
  };
  return map[t] ?? t;
}

function formatRuleDuration(duration: Record<string, unknown> | undefined): string {
  if (!duration || typeof duration !== 'object') return '—';
  const dateRange = duration.dateRangeTypeRuleDuration as Record<string, unknown> | null | undefined;
  const eventDur = duration.eventTypeRuleDuration as Record<string, unknown> | null | undefined;
  if (dateRange && dateRange.startDate && dateRange.endDate) {
    return `${formatAmazonYyyymmdd(String(dateRange.startDate))} → ${formatAmazonYyyymmdd(String(dateRange.endDate))}`;
  }
  if (eventDur && (eventDur.eventId || eventDur.eventName)) {
    const name = typeof eventDur.eventName === 'string' ? eventDur.eventName : 'Shopping event';
    const start = eventDur.startDate ? formatAmazonYyyymmdd(String(eventDur.startDate)) : '';
    const end = eventDur.endDate ? formatAmazonYyyymmdd(String(eventDur.endDate)) : '';
    const id =
      typeof eventDur.eventId === 'string' ? ` · ID ${eventDur.eventId.slice(0, 8)}…` : '';
    if (start && end) return `${name}: ${start} → ${end}${id}`;
    return `${name}${id}`;
  }
  return '—';
}

function formatRuleRecurrence(rec: Record<string, unknown> | undefined): string {
  if (!rec || typeof rec !== 'object') return '—';
  const type = String(rec.type ?? '');
  const days = rec.daysOfWeek;
  if (Array.isArray(days) && days.length > 0) {
    return `${type} · ${days.join(', ')}`;
  }
  const intra = rec.intraDaySchedule;
  if (Array.isArray(intra) && intra.length > 0) {
    const slots = intra.map((slot) => {
      const s = slot as Record<string, unknown>;
      return `${String(s.startTime ?? '?')}–${String(s.endTime ?? '?')}`;
    });
    return `${type} · ${slots.join('; ')}`;
  }
  return type || '—';
}

function formatMillis(ms: unknown): string {
  if (typeof ms !== 'number') return '—';
  try {
    return new Date(ms).toLocaleString();
  } catch {
    return String(ms);
  }
}

function RuleStatusPill({ label, value }: { label: string; value: string }) {
  const u = value.toUpperCase();
  const cls =
    u === 'ACTIVE'
      ? 'bg-success-subtle text-success-ink border-success/20'
      : u === 'EXPIRED' || u === 'ENDED'
        ? 'bg-surface-muted text-foreground-muted border-border'
        : 'bg-warning-subtle text-warning-ink border-warning/20';
  return (
    <span className="text-xs text-foreground-subtle">
      {label}:{' '}
      <span className={`inline-flex px-2 py-0.5 rounded border font-medium ${cls}`}>{value}</span>
    </span>
  );
}

export function BudgetRuleListCard({
  row,
  onEdit,
  onToggleState,
  togglingState = false,
  variant = 'full',
  profileId,
  product,
  profileCampaigns,
  onAssociationsChanged,
  excludeCampaignIds,
}: {
  row: Record<string, unknown>;
  onEdit?: (row: Record<string, unknown>) => void;
  onToggleState?: (row: Record<string, unknown>) => void;
  togglingState?: boolean;
  /** `compact` hides associations, rule id, status pill, and timestamps (campaign details). */
  variant?: 'full' | 'compact';
  profileId?: string;
  product?: CampaignAdProduct;
  profileCampaigns?: SpCampaignNormalized[];
  onAssociationsChanged?: () => void;
  /** Campaign ids always hidden from the add dropdown (e.g. current campaign on details page). */
  excludeCampaignIds?: string[];
}) {
  const ruleId = typeof row.ruleId === 'string' ? row.ruleId : '';
  const details =
    row.ruleDetails && typeof row.ruleDetails === 'object'
      ? (row.ruleDetails as Record<string, unknown>)
      : {};
  const name = typeof details.name === 'string' ? details.name : 'Unnamed rule';
  const ruleType = typeof details.ruleType === 'string' ? details.ruleType : '';
  const increase = details.budgetIncreaseBy as Record<string, unknown> | undefined;
  const increasePct =
    increase && typeof increase.value === 'number'
      ? increase.value
      : increase && typeof increase.value === 'string'
        ? parseFloat(increase.value)
        : null;
  const duration = details.duration as Record<string, unknown> | undefined;
  const recurrence = details.recurrence as Record<string, unknown> | undefined;
  const perf = details.performanceMeasureCondition as Record<string, unknown> | null | undefined;

  const ruleState = typeof row.ruleState === 'string' ? row.ruleState : '—';
  const ruleStatus = typeof row.ruleStatus === 'string' ? row.ruleStatus : '—';
  const ruleStateUpper = ruleState.toUpperCase();
  const canToggleState =
    !!onToggleState && !!ruleId && (ruleStateUpper === 'ACTIVE' || ruleStateUpper === 'PAUSED');

  const isCompact = variant === 'compact';
  const showAssociations = variant === 'full' && !!profileId && !!product && !!ruleId;
  const campaignsForLookup = profileCampaigns ?? [];
  const {
    associated,
    isLoading: associationsLoading,
    error: associationsError,
  } = useBudgetRuleAssociatedCampaigns(
    profileId ?? '',
    product ?? 'SP',
    ruleId,
    showAssociations
  );

  return (
    <div
      className={`rounded-xl border border-border bg-surface p-4 shadow-sm text-sm ${isCompact ? 'space-y-2' : 'space-y-3'}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h4 className="font-semibold text-foreground text-base">{name}</h4>
          {!isCompact ? (
            <p className="text-xs text-foreground-subtle font-mono mt-0.5">{ruleId || 'No rule id'}</p>
          ) : null}
        </div>
        <div className="flex flex-col items-end gap-2">
          {(onEdit || canToggleState) && (
            <div className="flex flex-wrap gap-2 justify-end">
              {onEdit && (
                <button
                  type="button"
                  onClick={() => onEdit(row)}
                  className="px-2.5 py-1 text-xs font-medium rounded border border-border text-foreground-muted hover:bg-page"
                >
                  Edit
                </button>
              )}
              {canToggleState && (
                <button
                  type="button"
                  onClick={() => onToggleState(row)}
                  disabled={togglingState}
                  className="px-2.5 py-1 text-xs font-medium rounded border border-brand/30 text-brand hover:bg-brand-subtle disabled:opacity-50"
                >
                  {togglingState ? '…' : ruleStateUpper === 'ACTIVE' ? 'Pause' : 'Resume'}
                </button>
              )}
            </div>
          )}
          <span className="text-xs font-medium text-brand bg-brand-subtle border border-brand/20 px-2 py-0.5 rounded">
            {ruleTypeLabel(ruleType)}
          </span>
          <div className="flex flex-wrap gap-2 justify-end">
            <RuleStatusPill label="State" value={ruleState} />
            {!isCompact ? <RuleStatusPill label="Status" value={ruleStatus} /> : null}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-foreground-muted">
        <div>
          <span className="text-foreground-subtle text-xs uppercase tracking-wide">Budget increase</span>
          <p className="font-medium">
            {increasePct != null && !Number.isNaN(increasePct) ? `${increasePct}%` : '—'}
            {increase?.type ? (
              <span className="text-foreground-subtle font-normal text-xs ml-1">({String(increase.type)})</span>
            ) : null}
          </p>
        </div>
        <div>
          <span className="text-foreground-subtle text-xs uppercase tracking-wide">Recurrence</span>
          <p>{formatRuleRecurrence(recurrence)}</p>
        </div>
        <div className="sm:col-span-2">
          <span className="text-foreground-subtle text-xs uppercase tracking-wide">Duration</span>
          <p>{formatRuleDuration(duration)}</p>
        </div>
        {perf && perf.metricName != null ? (
          <div className="sm:col-span-2 rounded-xl bg-page border border-chrome-divider px-3 py-2">
            <span className="text-foreground-subtle text-xs uppercase tracking-wide">Performance condition</span>
            <p className="mt-1">
              <span className="font-medium">{String(perf.metricName)}</span>{' '}
              {comparisonOperatorLabel(String(perf.comparisonOperator ?? ''))}{' '}
              <span className="font-medium">{String(perf.threshold ?? '—')}</span>
            </p>
          </div>
        ) : null}
        {showAssociations ? (
          <div className="sm:col-span-2 rounded-xl border border-chrome-divider bg-page/60 px-3 py-2.5">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
              <span className="text-foreground-subtle text-xs uppercase tracking-wide">
                Associated campaigns
              </span>
              {!associationsLoading && !associationsError ? (
                <span className="text-xs text-foreground-subtle">
                  {associated.length} linked
                </span>
              ) : null}
            </div>
            <BudgetRuleLinkedCampaignsChips
              associated={associated}
              profileCampaigns={campaignsForLookup}
              isLoading={associationsLoading}
              error={associationsError}
            />
          </div>
        ) : null}
      </div>

      {!isCompact ? (
        <div className="flex flex-wrap gap-4 text-xs text-foreground-subtle pt-1 border-t border-chrome-divider">
          <span>Created: {formatMillis(row.createdDate)}</span>
          <span>Updated: {formatMillis(row.lastUpdatedDate)}</span>
        </div>
      ) : null}

      {showAssociations ? (
        <BudgetRuleCampaignAssociations
          profileId={profileId!}
          product={product!}
          ruleId={ruleId}
          profileCampaigns={campaignsForLookup}
          onAssociationsChanged={onAssociationsChanged}
          associated={associated}
          isLoading={associationsLoading}
          loadError={associationsError}
          excludeCampaignIds={excludeCampaignIds}
        />
      ) : null}
    </div>
  );
}
